import { createFeature, createReducer, on } from '@ngrx/store';

import { loginApiActions } from './actions/login-api.actions';
import { loginFeatureActions } from './actions/login-feature.actions';
import { SSOHttpErrorResponse } from '../../models/errors.model';
import { TfaSignInProvider } from '../../models/account/tfa-sign-in-provider.enum';
import { userNameLocalStorageKey } from './user-name-local-storage-key.const';

export type LoginStep = 'login' | 'idpLogin' | 'twoFactorAuthentication' | 'passwordChange' | 'twoFactorAuthenticationForce' | '';

export type CoreState = {
  errorMessage: SSOHttpErrorResponse['error'];
  loadingActionState: boolean;
};

export interface LoginState {
  isTwoFactorRequired: boolean;
  hasPasswordExpired: boolean;
  passwordMatchesPolicy: boolean;
  isTwoFactorConfigurationRequired: boolean;
  twoFactorAuthenticationMethods: TfaSignInProvider[];
  defaultTwoFactorAuthenticationMethod: TfaSignInProvider | null;
  returnUrl: string | null;
  loginStep: LoginStep;
  loadingState: boolean;
  sendAgainLoading: boolean;
  errorMessage: SSOHttpErrorResponse['error'];
  rememberMe: boolean;
}

export const initialState: LoginState = {
  isTwoFactorRequired: false,
  passwordMatchesPolicy: true,
  isTwoFactorConfigurationRequired: false,
  hasPasswordExpired: false,
  twoFactorAuthenticationMethods: [],
  defaultTwoFactorAuthenticationMethod: null,
  returnUrl: null,
  loginStep: 'login',
  errorMessage: null,
  loadingState: false,
  sendAgainLoading: false,
  rememberMe: !!localStorage.getItem(userNameLocalStorageKey),
};

export const loginFeature = createFeature({
  name: 'loginFeature',
  reducer: createReducer(
    initialState,
    on(loginFeatureActions.login, loginFeatureActions.loginViaTwoFactor, loginFeatureActions.loginViaSingleSignOn, (state) => ({
      ...state,
      loadingState: true,
    })),
    on(loginFeatureActions.loginViaSingleSignOn, (state) => ({
      ...state,
      errorMessage: null,
    })),
    on(loginApiActions.loginSuccessfully, (state, { signInResponseModel }) => ({
      ...state,
      loadingState: false,
      errorMessage: null,
      isTwoFactorRequired: signInResponseModel.isTwoFactorRequired,
      hasPasswordExpired: signInResponseModel.hasPasswordExpired,
      passwordMatchesPolicy: signInResponseModel.passwordMatchesPolicy,
      isTwoFactorConfigurationRequired: signInResponseModel.isTwoFactorConfigurationRequired,
      twoFactorAuthenticationMethods: signInResponseModel.twoFactorAuthenticationMethods,
      defaultTwoFactorAuthenticationMethod: signInResponseModel.defaultTwoFactorAuthenticationMethod,
      returnUrl: signInResponseModel.returnUrl,
    })),
    on(loginApiActions.loginFailed, loginApiActions.loginViaTwoFactorFailed, (state, { error }) => ({
      ...state,
      loadingState: false,
      errorMessage: error,
    })),
    on(loginApiActions.loginViaTwoFactorSuccessfully, (state) => ({
      ...state,
      loginStep: 'twoFactorAuthentication' as LoginStep,
      loadingState: false,
    })),
    on(loginFeatureActions.changeTwoFactorAuthenticationMethod, (state, { method }) => ({
      ...state,
      defaultTwoFactorAuthenticationMethod: method,
    })),
    on(loginApiActions.loginViaSingleSignOnSuccessfully, (state) => ({ ...state, loadingState: false })),
    on(loginApiActions.loginViaSingleSignOnFailed, (state, { error }) => ({
      ...state,
      loadingState: false,
      errorMessage: error,
    })),
    on(loginFeatureActions.sendCodeAgain, (state) => ({ ...state, sendAgainLoading: true })),
    on(loginApiActions.sendingCodeAgainSuccessfully, (state) => ({ ...state, sendAgainLoading: false })),
    on(loginApiActions.sendingCodeAgainFailed, (state, { error }) => ({
      ...state,
      sendAgainLoading: false,
      errorMessage: error,
    })),
    on(loginFeatureActions.reCaptchaFailed, (state) => ({ ...state, loadingState: false })),
    on(loginFeatureActions.setLoginStep, (state, { step }) => ({ ...state, loginStep: step })),
    on(loginFeatureActions.clearMessages, (state) => ({ ...state, errorMessage: null })),
    on(loginFeatureActions.clearState, (state) => ({ ...state, ...initialState, rememberMe: state.rememberMe })),
    on(loginFeatureActions.setRememberMe, (state, { rememberMe }) => ({ ...state, rememberMe })),
  ),
});
